import React, { useState, useEffect, memo } from "react";
import { Product } from "@/types/product";
import { Customer } from "@/types/customer";
import { PaymentHistory } from "@/types/payment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogDatePicker } from "@/components/ui/dialog-date-picker";
import { CreditCard, Printer, Bell } from "lucide-react";

interface ProductPaymentCardProps {
  product: Product;
  index: number;
  customer: Customer;
  paymentHistory: PaymentHistory[];
  globalPaymentDate: Date;
  persistentNote?: string;
  noteSaveStatus?: 'saved' | 'unsaved' | 'saving';
  onSavePersistentNote: (productId: string, note: string) => Promise<void>;
  onPay: (payment: PaymentHistory, updatedProduct: Product, withPrint: boolean) => Promise<void>;
  confirm: any;
  showToast: (msg: string) => void;
}

const formatNumberWithCommas = (value: string | number): string => {
  const num = typeof value === 'string' ? value.replace(/,/g, '') : value.toString();
  if (!num || isNaN(Number(num))) return '';
  return Number(num).toLocaleString();
};

const parseFormattedNumber = (value: string): string => {
  return value.replace(/,/g, '');
};

const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const ProductPaymentCard = memo(function ProductPaymentCard({
  product,
  index,
  customer,
  paymentHistory,
  globalPaymentDate,
  persistentNote = "",
  noteSaveStatus = "unsaved",
  onSavePersistentNote,
  onPay,
  confirm,
  showToast,
}: ProductPaymentCardProps) {
  const productPaymentHistory = paymentHistory.filter(p => p.productId === product.id);
  const currentPaid = productPaymentHistory.reduce((sum, p) => sum + p.amount, 0);
  const currentRemaining = Math.max(0, product.salePrice - currentPaid);
  const maxHistoryInstallment = productPaymentHistory.length > 0
    ? Math.max(...productPaymentHistory.map(p => p.installmentNumber))
    : 0;
  const minInstallment = maxHistoryInstallment + 1;

  // Isolated local state for form typing to prevent parent re-renders
  const defaultPayAmount = Math.min(product.monthlyInstallment, currentRemaining);
  const [amountInput, setAmountInput] = useState<string>(defaultPayAmount > 0 ? defaultPayAmount.toString() : product.monthlyInstallment.toString());
  const [installmentInput, setInstallmentInput] = useState<number>(minInstallment);
  const [paymentDateInput, setPaymentDateInput] = useState<Date>(globalPaymentDate);
  const [paymentNoteInput, setPaymentNoteInput] = useState<string>("");
  const [localNote, setLocalNote] = useState<string>(persistentNote);
  const [localStatus, setLocalStatus] = useState<'saved' | 'unsaved' | 'saving'>(noteSaveStatus);

  useEffect(() => {
    setInstallmentInput(minInstallment);
  }, [minInstallment]);

  useEffect(() => {
    setPaymentDateInput(globalPaymentDate);
  }, [globalPaymentDate]);

  useEffect(() => {
    setLocalNote(persistentNote);
    setLocalStatus(noteSaveStatus);
  }, [persistentNote, noteSaveStatus]);

  const handleProcessPayment = async (withPrint: boolean) => {
    if (!amountInput) return;
    const amount = parseFloat(amountInput);
    if (amount <= 0) return;

    const currentProductPayments = paymentHistory.filter(p => p.productId === product.id);
    const existingPaid = currentProductPayments.reduce((sum, p) => sum + p.amount, 0);
    const availableRemaining = Math.max(0, product.salePrice - existingPaid);

    const payAmount = Math.min(amount, availableRemaining);
    if (payAmount <= 0) return;

    const actionText = withPrint ? "پارەدان و چاپکردن" : "پارەدان";
    const actionDesc = withPrint 
      ? `دڵنیایت لە پارەدانی ${payAmount.toLocaleString()} ${product.currency === "IQD" ? "د.ع" : "$"} بۆ ${product.deviceName} و چاپکردنی وەصڵ؟`
      : `دڵنیایت لە پارەدانی ${payAmount.toLocaleString()} ${product.currency === "IQD" ? "د.ع" : "$"} بۆ ${product.deviceName}؟`;

    await confirm({
      title: actionText,
      description: actionDesc,
      variant: "default",
      confirmText: withPrint ? "بەڵێ، پارە بدە و چاپ بکە" : "بەڵێ، پارە بدە",
      cancelText: "نەخێر",
      onConfirm: async () => {
        const newPaidAmount = existingPaid + payAmount;
        const remainingAfterPayment = Math.max(0, product.salePrice - newPaidAmount);
        const d = new Date(paymentDateInput);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const payment: PaymentHistory = {
          id: generateId(),
          customerId: customer.id,
          productId: product.id,
          productName: product.deviceName,
          amount: payAmount,
          currency: product.currency,
          date: dateStr,
          installmentNumber: installmentInput,
          invoiceNumber: 0,
          remainingAfterPayment,
          note: paymentNoteInput || undefined,
          createdAt: new Date().toISOString(),
        };

        const updatedProduct: Product = {
          ...product,
          paidAmount: newPaidAmount,
          remainingAmount: remainingAfterPayment,
          installments: installmentInput,
        };

        await onPay(payment, updatedProduct, withPrint);
        setPaymentNoteInput("");
        const nextDefaultAmount = Math.min(product.monthlyInstallment, remainingAfterPayment);
        setAmountInput(nextDefaultAmount > 0 ? nextDefaultAmount.toString() : product.monthlyInstallment.toString());
        showToast(`پارەدان نوێکراوەتەوە بۆ ${product.deviceName}`);
      }
    });
  };

  return (
    <div>
      {index > 0 && (
        <div className="flex items-center gap-3 py-3">
          <div className="flex-1 h-px bg-border"></div>
        </div>
      )}
      <div className={`rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-all ${index % 2 === 1 ? 'bg-muted/30' : ''}`}>
        {/* Product Header */}
        <div className="px-4 py-3 bg-muted/30 border-b">
          {/* Top row: number + name + description + notes */}
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-base">{index + 1}</span>
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base">{product.deviceName}</h3>
              {product.deviceDescription && (
                <p className="text-sm text-muted-foreground mt-0.5 truncate">{product.deviceDescription}</p>
              )}
              {product.notes && (
                <span className="inline-block mt-1 text-xs font-semibold text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-900">
                  {product.notes}
                </span>
              )}
            </div>
          </div>

          {/* Stats row: 4 metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <div className="text-center py-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900">
              <div className="text-[10px] text-muted-foreground mb-1 font-bold">نرخی فرۆشتن</div>
              <div className="text-xs sm:text-sm font-bold text-indigo-700 dark:text-indigo-400 flex items-center justify-center gap-1">
                <span dir="ltr">{product.salePrice.toLocaleString()}</span>
                <span className="text-[10px]">{product.currency === "IQD" ? "د.ع" : "$"}</span>
              </div>
            </div>
            <div className="text-center py-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
              <div className="text-[10px] text-muted-foreground mb-1 font-bold">قیستی مانگانە</div>
              <div className="text-xs sm:text-sm font-bold text-blue-600 flex items-center justify-center gap-1">
                <span dir="ltr">{product.monthlyInstallment.toLocaleString()}</span>
                <span className="text-[10px]">{product.currency === "IQD" ? "د.ع" : "$"}</span>
              </div>
            </div>
            <div className="text-center py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900">
              <div className="text-[10px] text-muted-foreground mb-1 font-bold">وەرگیراو</div>
              <div className="text-xs sm:text-sm font-bold text-emerald-600 flex items-center justify-center gap-1">
                <span dir="ltr">{product.paidAmount.toLocaleString()}</span>
                <span className="text-[10px]">{product.currency === "IQD" ? "د.ع" : "$"}</span>
              </div>
            </div>
            <div className="text-center py-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
              <div className="text-[10px] text-muted-foreground mb-1 font-bold">ماوە</div>
              <div className="text-xs sm:text-sm font-bold text-red-600 flex items-center justify-center gap-1">
                <span dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                <span className="text-[10px]">{product.currency === "IQD" ? "د.ع" : "$"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <div className="px-4 py-3 bg-muted/20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            {/* Payment Amount + Next Installment — always side by side in one row */}
            <div className="flex gap-2">
              <div className="flex-1 rounded-lg bg-background border border-border p-2.5">
                <Label className="text-[11px] mb-1.5 block text-muted-foreground font-semibold">بڕی پارەدان</Label>
                <Input
                  type="text"
                  placeholder="بڕ"
                  dir="ltr"
                  className="h-9 text-sm font-bold"
                  value={formatNumberWithCommas(amountInput)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d+(\.\d*)?$/.test(rawValue)) {
                      const numValue = parseFloat(rawValue) || 0;
                      if (numValue > product.remainingAmount) {
                        setAmountInput(product.remainingAmount.toString());
                      } else {
                        setAmountInput(rawValue);
                      }
                    }
                  }}
                />
              </div>
              <div className="flex-1 rounded-lg bg-background border border-border p-2.5">
                <Label className="text-[11px] mb-1.5 block text-muted-foreground font-semibold">جاری داهاتوو</Label>
                <Input
                  type="number"
                  min={minInstallment}
                  value={installmentInput}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0;
                    if (newValue >= minInstallment) {
                      setInstallmentInput(newValue);
                    }
                  }}
                  className="h-9 text-sm font-bold text-center"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Date */}
            <div className="rounded-lg bg-background border border-border p-2.5">
              <Label className="text-[11px] mb-1.5 block text-muted-foreground font-semibold">بەروار</Label>
              <DialogDatePicker
                value={paymentDateInput}
                onChange={(date) => setPaymentDateInput(date)}
                label=""
              />
            </div>

            {/* Note */}
            <div className="rounded-lg bg-background border border-border p-2.5">
              <Label className="text-[11px] mb-1.5 block text-muted-foreground font-semibold">تێبینی پارەدان</Label>
              <Input
                type="text"
                placeholder="ئارەزوومەندانە"
                className="h-9 text-sm"
                value={paymentNoteInput}
                onChange={(e) => setPaymentNoteInput(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-stretch md:items-end gap-3">
            {/* Notification / Persistent Note */}
            <div className="flex-1 rounded-lg border-2 p-2.5 transition-all" style={{
              backgroundColor: localStatus === 'saved' ? 'hsl(var(--background))' : 'hsl(var(--background))',
              borderColor: localStatus === 'saved' ? '#10b981' : '#ef4444'
            }}>
              <div className="flex items-center gap-2 mb-1.5">
                <Bell className="h-3.5 w-3.5" style={{
                  color: localStatus === 'saved' ? '#10b981' : '#ef4444'
                }} />
                <Label className="text-[11px] font-semibold" style={{
                  color: localStatus === 'saved' ? '#10b981' : '#ef4444'
                }}>ئاگادارکردنەوە</Label>
                <div className="h-2 w-2 rounded-full" style={{
                  backgroundColor: localStatus === 'saved' ? '#10b981' : '#ef4444'
                }}></div>
                <span className="text-[10px] font-medium mr-auto" style={{
                  color: localStatus === 'saved' ? '#10b981' : '#ef4444'
                }}>
                  {localStatus === 'saved' ? '✓ سەیڤکراوە' : '⚠ سەیڤ نەکراوە'}
                </span>
              </div>
              <Input
                type="text"
                placeholder="ئاگادارکردنەوە بۆ ئەم ئایتمە..."
                className="h-9 text-sm"
                style={{
                  borderColor: localStatus === 'saved' ? '#10b981' : '#ef4444'
                }}
                value={localNote}
                onChange={(e) => {
                  setLocalNote(e.target.value);
                  setLocalStatus('unsaved');
                }}
                onBlur={() => {
                  setLocalStatus('saving');
                  onSavePersistentNote(product.id, localNote)
                    .then(() => setLocalStatus(localNote ? 'saved' : 'unsaved'))
                    .catch(() => setLocalStatus('unsaved'));
                }}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
              <Button
                variant="default"
                size="sm"
                className="flex-1 md:flex-none h-9 px-4"
                onClick={() => handleProcessPayment(false)}
              >
                <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                پارەدان
              </Button>

              <Button
                variant="secondary"
                size="sm"
                className="flex-1 md:flex-none h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleProcessPayment(true)}
              >
                <Printer className="h-3.5 w-3.5 mr-1.5" />
                پارەدان و چاپکردن
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
